import { Status, NonClientError, ServerError } from 'hooks/requests/fetchUtils';
import useFetchApi from 'hooks/useFetchApi';
import { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import useFmcHost from 'hooks/useFmcHost';

import {
  InitialFinanceOffer,
  setInitialFinanceOffers,
} from 'state/initialFinanceOffers';

import { receiveApplicationResult, ApplicationResultType } from 'fmc-sdk';
import catchOnly from 'utils/catchOnly';
import useQueryParameters from 'hooks/useQueryParameters';
import useApplicationBasedOverrides from 'hooks/useApplicationBasedOverrides';

const PATH = '/api/unified_initial_offers';

type UnifiedInitialOffersHook = () => [
  () => Promise<UnifiedInitialOffersResult>,
  Status,
  boolean,
];

export type UnifiedInitialOffersResult = {
  paymentProducts: InitialFinanceOffer[];
};

export class UnifiedInitialOffersError extends Error {}

const useUnifiedInitialOffers: UnifiedInitialOffersHook = () => {
  const [error, setError] = useState<boolean>(false);
  const [status, fetch] = useFetchApi();
  const dispatch = useDispatch();
  const fmcDispatch = useFmcHost();
  const { mockUIO } = useQueryParameters();
  const overrideAttributes = useApplicationBasedOverrides();

  const unifiedInitialOffersRequest = useCallback(async (): Promise<
    UnifiedInitialOffersResult
  > => {
    setError(false);

    try {
      // TODO: Missing params include clientVariable4. I have
      // a question out to Tim to confirm whether these are still needed.
      const response = await fetch(PATH, {
        method: 'POST',
        body: {
          ...overrideAttributes,
          mockResponse: mockUIO,
        },
      });

      if (response.ok) {
        const initialOffers: UnifiedInitialOffersResult = await response.json();

        dispatch(setInitialFinanceOffers(initialOffers.paymentProducts));
        return Promise.resolve(initialOffers);
      } else {
        // Expect this is to be a 4xx error, in which case
        // the server is expected to return an error object.
        // So we'll attempt to parse it and get at the error message,
        // or just return the entire body if it cannot be parsed.

        const body = await response.text();
        let message = body;
        try {
          message = JSON.parse(message).message;
        } catch (e) {
          /* No need to do anything */
        }

        fmcDispatch(
          receiveApplicationResult({
            result: ApplicationResultType.Error,
            callId: null,
            message,
            status: response.status,
          }),
        );

        setError(true);
        return Promise.reject(new UnifiedInitialOffersError());
      }
    } catch (e) {
      if (catchOnly(e, NonClientError)) {
        // Let FmcHost know about server and network errors.
        let status = 0;
        let message = 'NetworkError';

        if (e instanceof ServerError) {
          status = e.response.status;
          message = await e.response.text();
        }

        fmcDispatch(
          receiveApplicationResult({
            result: ApplicationResultType.Error,
            callId: null,
            status: status,
            message: message,
          }),
        );
      }
      // Re-throw so that any exceptions can be handled downstream.
      throw e;
    }
  }, [fetch, dispatch, fmcDispatch, mockUIO, overrideAttributes]);

  return [unifiedInitialOffersRequest, status, error];
};

export default useUnifiedInitialOffers;
